'use client';
import { useEffect, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import styled, { keyframes, css } from 'styled-components';
import { Image } from '@/shared/ui/image/Image';

interface AccordionSection {
  header: string;
  content: Node[];
}

interface AccordionPageProps {
  html: string;
  isExpanded?: boolean;
  smallH1?: boolean;
}

export const AccordionPage = ({
  html,
  isExpanded = false,
  smallH1 = false,
}: AccordionPageProps) => {
  const [page, setPage] = useState<{
    intro: Node[];
    items: AccordionSection[];
  } | null>(null);
  const [items, setItems] = useState<AccordionSection[]>([]);

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const elements = Array.from(doc.body.childNodes);

    const intro: Node[] = [];
    const result: AccordionSection[] = [];
    let currentHeader: string | null = null;
    let currentContent: Node[] = [];

    elements.forEach((node) => {
      if (node.nodeName.toLowerCase() === 'h2') {
        if (currentHeader) {
          result.push({ header: currentHeader, content: currentContent });
        }
        currentHeader = node.textContent;
        currentContent = [];
      } else {
        if (currentHeader) {
          currentContent.push(node.cloneNode(true));
        } else {
          intro.push(node.cloneNode(true));
        }
      }
    });

    if (currentHeader && currentContent.length > 0) {
      result.push({ header: currentHeader, content: currentContent });
    }

    setItems(result);
    setPage({ intro: intro, items: result });
  }, [html]);

  if (items.length === 0 || !page) {
    return null;
  }

  const serializer = new XMLSerializer();

  const renderSectionContent = (content: Node[]) =>
    content.map((node, nodeIndex) => {
      const htmlContent = serializer.serializeToString(node);
      return (
        <div
          key={nodeIndex}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      );
    });

  return (
    <HtmlContent $isVariant2={smallH1}>
      {(() => {
        // Фильтруем и сериализуем только нужные узлы
        let htmlString = '';
        for (const node of page.intro) {
          // Пропускаем пустые текстовые узлы
          if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) {
            continue;
          }
          // Пропускаем комментарии
          if (node.nodeType === Node.COMMENT_NODE) {
            continue;
          }
          // Сериализуем элементы и непустые текстовые узлы
          htmlString += serializer.serializeToString(node);
        }

        // Вставляем весь HTML за раз, без лишних div
        return htmlString ? (
          <div
            className={isExpanded ? '' : 'intro'}
            dangerouslySetInnerHTML={{ __html: htmlString }}
          />
        ) : null;
      })()}

      {/*{page.intro.map((node) => {*/}
      {/*  const htmlContent = new XMLSerializer().serializeToString(node);*/}
      {/*  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;*/}
      {/*})}*/}
      {isExpanded ? (
        // Всегда развёрнуто, без схлопывания — просто плоские секции без Accordion.
        items.map(({ header, content }, sectionIndex) => (
          <FlatSection key={sectionIndex}>
            <h2>{header}</h2>
            {renderSectionContent(content)}
          </FlatSection>
        ))
      ) : (
        <AccordionRoot type="multiple" collapsible="true">
          {items.map(({ header, content }, sectionIndex) => (
            <AccordionItem key={sectionIndex} value={header}>
              <AccordionTrigger>
                {header}
                <ChevronWrapper>
                  <Image src="/images/icons/caretDown.svg" alt="caretIcon" />
                </ChevronWrapper>
              </AccordionTrigger>
              <AccordionContent>
                {/*<div style={{ paddingTop: '12px' }}>*/}
                {renderSectionContent(content)}
                {/*</div>*/}
              </AccordionContent>
            </AccordionItem>
          ))}
        </AccordionRoot>
      )}
    </HtmlContent>
  );
};

const listAndTableStyles = css`
  ul {
    //list-style-type: disc;
    padding-left: 16px;
    //margin-bottom: 16px;
  }

  tr:nth-child(even) {
    //background-color: #333333;
    background-color: #252420;
    background-color: #232226;
  }

  table {
    width: 100%;
    //border: 1px solid;
  }
`;
const h2Styles = css`
  h2 {
    color: #fff;

    /* Desktop/Body Text/Medium/C */
    //font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 140%; /* 19.6px */
  }
`;
const bodyTextStyles = css`
  p,
  td,
  li {
    color: #afafaf;

    /* Desktop/Body Text/Regular/C */
    //font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 19.6px */

    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
  }

  th {
    color: #afafaf;

    /* Desktop/Body Text/Regular/C */
    //font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: bold;
    line-height: 140%; /* 19.6px */
  }
`;
const HtmlContentBase = css`
  display: flex;
  position: relative;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px;
  align-self: stretch;
  overflow: auto;

  ul,
  ol {
    list-style-position: inside;
    padding-left: 16px;
  }

  ${listAndTableStyles}

  th {
    background-color: #232226;
  }

  th,
  td {
    padding: 8px;
    border-bottom: 1px solid #888;
  }

  ${h2Styles}

  h3 {
    line-height: 140%;
    padding: 5px 0 0 0;
  }

  .intro p:first-of-type {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  ${bodyTextStyles}
`;
const H1Variant1 = css`
  h1 {
    color: #fff;

    /* Desktop/Heading/Medium/H5 */
    //font-family: Rubik;
    font-size: 28px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 33.6px */

    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
    margin-bottom: 20px;
  }
`;
const H1Variant2 = css`
  h1 {
    color: #fff;

    /* Desktop/Body Text/Medium/B */
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 140%; /* 22.4px */
  }
`;
const HtmlContent = styled.div<{ $isVariant2?: boolean }>`
  ${HtmlContentBase}
  ${({ $isVariant2 }) => ($isVariant2 ? H1Variant2 : H1Variant1)}
`;

const FlatSection = styled.div`
  width: 100%;
  padding: 20px 0 0 0;

  h2 {
    color: #fff;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 140%;
    margin: 0 0 8px 0;
  }
`;

const AccordionRoot = styled(Accordion.Root)`
  width: 100%;
`;
const AccordionItem = styled(Accordion.Item)`
  color: #fff;

  /* Desktop/Body Text/Medium/C */
  //font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 19.6px */
  letter-spacing: 0;
  padding: 20px 0 0 0;
`;
const AccordionTrigger = styled(Accordion.Trigger)`
  all: unset;
  display: flex;
  justify-content: space-between;
  width: 100%;

  &:hover {
    //background-color: #000;
  }
`;
const ChevronWrapper = styled.span`
  display: inline-block;
  transition: transform 0.3s ease;

  [data-state='open'] > & {
    transform: rotate(180deg);
  }
`;
const slideDown = keyframes`
    from {
        height: 0;
    }
    to {
        height: var(--radix-accordion-content-height);
    }
`;

const slideUp = keyframes`
    from {
        height: var(--radix-accordion-content-height);
    }
    to {
        height: 0;
    }
`;
const AccordionContent = styled(Accordion.Content)`
  color: #afafaf;

  /* Desktop/Body Text/Regular/C */
  //font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
  letter-spacing: 0;

  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;

  overflow: hidden;

  &[data-state='open'] {
    animation: ${slideDown} 300ms ease-out forwards;
  }

  &[data-state='closed'] {
    animation: ${slideUp} 300ms ease-out forwards;
  }

  ul,
  ol {
    list-style-position: inside;
  }

  ${listAndTableStyles}

  th {
    background-color: #232226;
    border-bottom: 1px solid #888;
  }

  th,
  td {
    padding: 8px;
    //border-bottom: 1px solid #888;
  }

  h1 {
    color: #fff;

    /* Desktop/Heading/Medium/H5 */
    //font-family: Rubik;
    font-size: 28px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 33.6px */

    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
  }

  ${h2Styles}

  ${bodyTextStyles}
`;
